type CartSyncItem = {
	id: string;
	quantity: number;
	crushed?: boolean;
};

export type CartSyncGroup = {
	id: string;
	kind: string;
	vendor: { id: string };
	itemIds: readonly string[];
};

type CartSyncComparable = {
	cartId?: number | null;
	deliveryMethodUuid?: string | null;
	vendor?: { id?: string | null } | null;
	items: readonly CartSyncItem[];
	fulfillmentGroups?: readonly CartSyncGroup[] | null;
};

/**
 * How the basket is split into packages, as one comparable value.
 *
 * The two places that ask whether a cart has moved on — the cross-sell mirror
 * and the checkout store's staleness check — both used to compare items, vendor
 * and the cart-level delivery method alone. A basket that stopped spanning two
 * vendors therefore looked unchanged to both, and checkout went on routing to
 * the delivery step for a package the server had already dropped (FR-033,
 * OTC-196). Shared rather than written twice so the two cannot drift apart
 * again, which is the shape that defect took.
 *
 * A group's own `deliveryMethodUuid` is deliberately absent. The delivery step
 * patches it into the stored cart with no server write, so including it would
 * make the step look stale to itself and re-initialise the checkout underneath
 * the customer.
 */
export function getFulfillmentGroupsFingerprint(
	groups: readonly CartSyncGroup[] | null | undefined,
): string {
	return (groups ?? [])
		.map((group) =>
			[
				group.id,
				group.kind,
				group.vendor.id,
				[...group.itemIds].sort((a, b) => a.localeCompare(b)).join(","),
			].join(":"),
		)
		.sort((a, b) => a.localeCompare(b))
		.join("|");
}

function getCartSyncFingerprint(cart: CartSyncComparable): string {
	const items = [...cart.items]
		.map((item) => `${item.id}:${item.quantity}:${item.crushed ? "1" : "0"}`)
		.sort((a, b) => a.localeCompare(b))
		.join("|");

	return [
		cart.cartId ?? "",
		cart.vendor?.id ?? "",
		cart.deliveryMethodUuid ?? "",
		items,
		getFulfillmentGroupsFingerprint(cart.fulfillmentGroups),
	].join(";");
}

export function isStoredCheckoutCartStale(
	storedCart: CartSyncComparable,
	currentCart: CartSyncComparable,
): boolean {
	return (
		getCartSyncFingerprint(storedCart) !== getCartSyncFingerprint(currentCart)
	);
}
